import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, Upload, User, Bell, Mail, Link2, Sparkles, FileText, Award, FileCog, Brain, Briefcase, Linkedin, Zap, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SettingsDataProvider, useSettingsData } from '../../../contexts/SettingsDataContext';

const SettingsLayoutInner = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { preloadAll, loading } = useSettingsData();

  const menuItems = [
    {
      title: 'Bulk Upload',
      icon: Upload,
      path: '/recruiter/settings/bulk-upload',
    },
    {
      title: 'Skills Management',
      icon: Award,
      path: '/recruiter/settings/skills',
    },
    {
      title: 'Positions Management',
      icon: Briefcase,
      path: '/recruiter/settings/positions',
    },
    {
      title: 'Profile',
      icon: User,
      path: '/recruiter/settings/profile',
    },
    {
      title: 'Notifications',
      icon: Bell,
      path: '/recruiter/settings/notifications',
    },
    {
      title: 'Integrations',
      icon: Link2,
      path: '/recruiter/settings/integrations',
    },
    {
      title: 'LinkedIn Templates',
      icon: Linkedin,
      path: '/recruiter/settings/linkedin-templates',
    },
    {
      title: 'Matching Settings',
      icon: Sparkles,
      path: '/recruiter/settings/matching',
    },
    {
      title: 'Automation',
      icon: Zap,
      path: '/recruiter/settings/automation',
    },
    {
      title: 'Candidate Requests',
      icon: Clock,
      path: '/recruiter/settings/candidate-requests',
    },
    {
      title: 'AI Configuration',
      icon: Brain,
      path: '/recruiter/settings/ai',
    },
    {
      title: 'Email Templates',
      icon: FileText,
      path: '/recruiter/settings/email-templates',
    },
    {
      title: 'PDF Templates',
      icon: FileCog,
      path: '/recruiter/settings/pdf-templates',
    },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Preload all settings data when component mounts
  useEffect(() => {
    preloadAll();
  }, [preloadAll]);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center">
          <Settings className="h-6 w-6 md:h-8 md:w-8 mr-2 md:mr-3" />
          Settings
        </h1>
        <p className="text-muted-foreground mt-1 md:mt-2 text-sm md:text-base">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Settings Container */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:space-x-12 lg:space-y-0">
        {/* Sidebar Navigation - horizontal scroll on mobile, vertical on desktop */}
        <aside className="lg:w-1/5 -mx-3 md:mx-0">
          <nav className="flex overflow-x-auto pb-2 px-3 md:px-0 lg:flex-col lg:overflow-visible lg:pb-0 gap-1 lg:gap-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'inline-flex items-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
                    'justify-start px-3 py-2 lg:px-4 lg:w-full shrink-0',
                    active
                      ? 'bg-muted hover:bg-muted'
                      : 'hover:bg-muted/50'
                  )}
                >
                  <Icon className="mr-2 h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">{item.title}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 lg:max-w-7xl min-w-0">
          {children}
        </div>
      </div>
    </div>
  );
};

// Wrap with provider to enable data preloading and caching
const SettingsLayout = ({ children }) => {
  return (
    <SettingsDataProvider>
      <SettingsLayoutInner>{children}</SettingsLayoutInner>
    </SettingsDataProvider>
  );
};

export default SettingsLayout;
